import React, { useEffect, useRef, useState } from 'react';
import { AgentDefinition, AgentOptions } from '../types';

interface ChatToolbarProps {
  agent: AgentDefinition | null;
  agents: AgentDefinition[];
  options: AgentOptions;
  optionsLoading: boolean;
  promptsError: string | null;
  modelsError: string | null;
  selectedPromptMode: string | null;
  selectedModel: string | null;
  conversationStarted: boolean;
  onAgentChange: (a: AgentDefinition) => void;
  onPromptModeChange: (id: string) => void;
  onModelChange: (id: string) => void;
}

export const ChatToolbar: React.FC<ChatToolbarProps> = ({
  agent, agents, options, optionsLoading,
  promptsError, modelsError,
  selectedPromptMode, selectedModel,
  conversationStarted,
  onAgentChange, onPromptModeChange, onModelChange,
}) => {
  const [agentPickerOpen, setAgentPickerOpen] = useState(false);
  const [promptPickerOpen, setPromptPickerOpen] = useState(false);
  const [modelPickerOpen, setModelPickerOpen] = useState(false);
  const agentRef = useRef<HTMLDivElement>(null);
  const promptRef = useRef<HTMLDivElement>(null);
  const modelRef = useRef<HTMLDivElement>(null);

  // Close any picker on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (agentRef.current && !agentRef.current.contains(e.target as Node)) setAgentPickerOpen(false);
      if (promptRef.current && !promptRef.current.contains(e.target as Node)) setPromptPickerOpen(false);
      if (modelRef.current && !modelRef.current.contains(e.target as Node)) setModelPickerOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const liveAgents = agents.filter((a) => a.mode === 'portal-chat');

  // Disabled only when URL is absent — if URL is present but fetch failed, keep enabled
  const promptDisabled = !agent?.endpoints?.promptsUrl;
  const promptTitle = !agent?.endpoints?.promptsUrl
    ? 'Agent has no prompt modes'
    : promptsError
    ? `Couldn't load prompt modes — using server default (${promptsError})`
    : optionsLoading
    ? 'Loading prompt modes…'
    : undefined;

  const modelDisabled = !agent?.endpoints?.modelsUrl;
  const modelTitle = !agent?.endpoints?.modelsUrl
    ? 'Agent has no model selection'
    : modelsError
    ? `Couldn't load models — using server default (${modelsError})`
    : optionsLoading
    ? 'Loading models…'
    : undefined;

  const selectedPromptLabel = options.promptModes.find((p) => p.id === selectedPromptMode)?.label
    ?? (optionsLoading ? '…' : promptsError ? 'Default' : '—');
  const selectedModelLabel = options.supportedModels.find((m) => m.id === selectedModel)?.name
    ?? (optionsLoading ? '…' : modelsError ? 'Default' : '—');

  return (
    <div className="chat-toolbar">

      {/* Agent pill */}
      <div className="chat-toolbar__picker-wrap" ref={agentRef}>
        <button
          className={`chat-toolbar__pill${conversationStarted ? ' chat-toolbar__pill--locked' : ''}`}
          onClick={() => !conversationStarted && setAgentPickerOpen((o) => !o)}
          title={conversationStarted ? 'Start a new conversation to switch agents' : 'Switch agent'}
          aria-haspopup="listbox"
          aria-expanded={agentPickerOpen}
        >
          {agent ? (
            <>
              <span className="chat-toolbar__avatar" style={{ background: agent.color }}>{agent.icon}</span>
              <span className="chat-toolbar__pill-label">{agent.name}</span>
            </>
          ) : (
            <span className="chat-toolbar__pill-label chat-toolbar__pill-label--none">Select agent</span>
          )}
          {conversationStarted ? (
            <span className="chat-toolbar__lock">🔒</span>
          ) : (
            <span className="chat-toolbar__caret">▾</span>
          )}
        </button>
        {agentPickerOpen && (
          <div className="chat-toolbar__dropdown" role="listbox" aria-label="Select agent">
            {liveAgents.map((a) => (
              <button
                key={a.id}
                className={`chat-toolbar__dropdown-item${agent?.id === a.id ? ' chat-toolbar__dropdown-item--active' : ''}`}
                onClick={() => { onAgentChange(a); setAgentPickerOpen(false); }}
                role="option"
                aria-selected={agent?.id === a.id}
              >
                <span className="chat-toolbar__avatar" style={{ background: a.color }}>{a.icon}</span>
                {a.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Mode pill */}
      <div className="chat-toolbar__picker-wrap" ref={promptRef}>
        <button
          className={`chat-toolbar__pill${promptDisabled ? ' chat-toolbar__pill--disabled' : ''}`}
          disabled={promptDisabled}
          onClick={() => !promptDisabled && setPromptPickerOpen((o) => !o)}
          title={promptTitle}
          aria-haspopup="listbox"
          aria-expanded={promptPickerOpen}
        >
          <span className="chat-toolbar__pill-dim">Mode</span>
          <span className="chat-toolbar__pill-label">{selectedPromptLabel}</span>
          {!promptDisabled && <span className="chat-toolbar__caret">▾</span>}
        </button>
        {promptPickerOpen && options.promptModes.length > 0 && (
          <div className="chat-toolbar__dropdown" role="listbox" aria-label="Select prompt mode">
            {options.promptModes.map((p) => (
              <button
                key={p.id}
                className={`chat-toolbar__dropdown-item${selectedPromptMode === p.id ? ' chat-toolbar__dropdown-item--active' : ''}`}
                onClick={() => { onPromptModeChange(p.id); setPromptPickerOpen(false); }}
                role="option"
                aria-selected={selectedPromptMode === p.id}
                title={p.description}
              >
                {p.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Model pill */}
      <div className="chat-toolbar__picker-wrap" ref={modelRef}>
        <button
          className={`chat-toolbar__pill${modelDisabled ? ' chat-toolbar__pill--disabled' : ''}`}
          disabled={modelDisabled}
          onClick={() => !modelDisabled && setModelPickerOpen((o) => !o)}
          title={modelTitle}
          aria-haspopup="listbox"
          aria-expanded={modelPickerOpen}
        >
          <span className="chat-toolbar__pill-dim">Model</span>
          <span className="chat-toolbar__pill-label">{selectedModelLabel}</span>
          {!modelDisabled && <span className="chat-toolbar__caret">▾</span>}
        </button>
        {modelPickerOpen && options.supportedModels.length > 0 && (
          <div className="chat-toolbar__dropdown" role="listbox" aria-label="Select model">
            {options.supportedModels.map((m) => (
              <button
                key={m.id}
                className={`chat-toolbar__dropdown-item${selectedModel === m.id ? ' chat-toolbar__dropdown-item--active' : ''}`}
                onClick={() => { onModelChange(m.id); setModelPickerOpen(false); }}
                role="option"
                aria-selected={selectedModel === m.id}
                title={m.description}
              >
                {m.name}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
